/**
 * PageTranslateBarV2 — full-width, high-contrast bar that houses the
 * unified TranslateWidget. Deliberately prominent (dark background, larger
 * label) because a huge share of our audience (India, Asia, Europe,
 * Middle East) benefits from an obvious way to read the page in their
 * native language.
 *
 * On IndianFamilyTree.com the right-hand CTA pair is "Build Your Family
 * Tree" + "Create a Memorial" (the two most important actions on IFT).
 * Every other host still sees the single "Grow Your Business with
 * Social Pulse" chip so we don't disrupt existing SocialPulse/FoodPulse
 * conversion.
 */
import { Link } from "react-router-dom";
import { Languages, ArrowRight, Building2, TreePine, Flower2 } from "lucide-react";
import TranslateWidget from "@/components/TranslateWidget";
import { resolveSiteSlug } from "@/config/siteConfig";

export default function PageTranslateBarV2() {
  const isIft = (() => {
    try {
      const q = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("site")
        : null;
      return (resolveSiteSlug({ queryParam: q || "" }) || "").toLowerCase().includes("indianfamilytree");
    } catch { return false; }
  })();

  return (
    <div
      data-testid="page-translate-bar-v2"
      className="w-full border-b"
      style={{
        background: "linear-gradient(90deg, #1F2937 0%, #111827 55%, #1F2937 100%)",
        borderColor: "rgba(255,255,255,0.08)",
        color: "#FDFBF7",
      }}
    >
      <div className="max-w-[1400px] mx-auto flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3">
        {/* LEFT — bold translate CTA + widget */}
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <div className="inline-flex items-center gap-2">
            <Languages className="w-5 h-5" style={{ color: "#FDBA74" }} aria-hidden />
            <span
              className="font-display text-base sm:text-lg leading-tight"
              data-testid="translate-headline"
            >
              Read this page in your language
            </span>
          </div>
          <TranslateWidget className="translate-widget-dark" />
        </div>

        {/* RIGHT — IFT-specific dual CTA (Family Tree + Memorial), or
            legacy single Grow-Your-Business chip on every other host. */}
        {isIft ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="ift-primary-ctas">
            <Link
              to="/family-trees/new"
              data-testid="ift-cta-family-tree"
              className="group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
              style={{ background: "#FDBA74", color: "#1F2937" }}
            >
              <TreePine className="w-4 h-4" aria-hidden />
              <span>Build Your Family Tree</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
            <Link
              to="/obituaries/submit"
              data-testid="ift-cta-memorial"
              className="group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
              style={{ background: "#FFFDF8", color: "#1F2937" }}
            >
              <Flower2 className="w-4 h-4" aria-hidden />
              <span>Celebrate a Life — Memorial</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
          </div>
        ) : (
          <Link
            to="/businesses"
            data-testid="grow-business-cta"
            className="group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
            style={{
              background: "#FDBA74",
              color: "#1F2937",
            }}
          >
            <Building2 className="w-4 h-4" aria-hidden />
            <span>Grow Your Business with Social Pulse</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </Link>
        )}
      </div>
    </div>
  );
}
